import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { Snackbar, AlertColor } from '@mui/material';
import { Alert } from '@ui-components/ui-library';

interface SnackbarContextType {
  showSnackbar: (message: string, title: string, severity?: AlertColor, duration?: number) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

interface SnackbarProviderProps {
  children: ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('success');
  const [duration, setDuration] = useState(3000);

  const showSnackbar = useCallback(
    (msg: string, title: string, sev: AlertColor = 'success', dur = 3000) => {
      setMessage(msg);
      setTitle(title);
      setSeverity(sev);
      setDuration(dur);
      setOpen(true);
    },
    []
  );

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        open={open}
        sx={{ top: '75px !important' }}
        autoHideDuration={duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          title={title}
          onClose={handleClose}
          severity={severity}
          elevation={6}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
